import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AfterViewChecked, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatChipInputEvent } from '@angular/material/chips';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { AttributeKey, AttributeKeys, CustomAttributes, VideoAttributesMapping } from '@yoimo/interfaces';
import { isEqual } from 'lodash';
import { firstValueFrom } from 'rxjs';
import { AddedAttributesType, AttributeEmitType} from '../../interfaces/attribute';
import { ChannelDetails } from '../../shared/channels.service';
import { DialogService } from '../../shared/dialog.service';
import { MasterChannelService } from '../../shared/master-channel.service';
import { TeamsService } from '../../shared/teams.service';
import { VideoAttributesService } from '../../shared/video-attributes.service';
import { SuccessPopupComponent } from '../success-popup/success-popup.component';

@Component({
  selector: 'app-team-tags',
  templateUrl: './team-tags.component.html',
  styleUrls: ['./team-tags.component.scss'],
})
export class TeamTagsComponent implements OnInit, AfterViewChecked {
  @ViewChild('tagInput') tagInput!: ElementRef<HTMLInputElement>;

  defaultTags?: string[] = [];
  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  ddName = '';
  ddOpen = false;
  searchText= '';
  selectedSource: Record<string, string> = {};
  isSpinner = false;
  isCreate = false;
  channelDetails: Partial<ChannelDetails> = {};
  isDefaultEventCleared = false;
  isTagsEnabled = false;
  isEventsEnabled = false;
  isAttributesEnabled = false;
  isLoading = true;

  addedAttributes: AddedAttributesType[] = [];
  videoAttributes: VideoAttributesMapping = {
    mappings: {},
  };
  videoAttributesLength = 0;
  selectedAttributeValueKeys: string[] = [];
  savedAttributes: CustomAttributes = {};
  currentAttributes: CustomAttributes = {};
  invalidAttributes = false;
  originalAttributes: CustomAttributes = {};
  filterList?: { name: string; docId: string; }[] = [];
  eventSourceDocs!: { name: string; docId: string; }[];
  filteredTagSuggestions: string[] = [];
  allTags: string[] = [];
  tagInputValue = '';

  constructor(
    private readonly dialogRef: MatDialogRef<TeamTagsComponent>,
    private readonly masterChannelService: MasterChannelService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      channelDocId: string;
      teamDocId: string;
      defaultTags: string[];
      defaultEventId: string;
    },
    private readonly teamService: TeamsService,
    private readonly dialog: MatDialog,
    private readonly dialogService: DialogService,
    private readonly videoAttributesService: VideoAttributesService
  ) { }

  ngOnInit(): void {
    this.initializeComponent();
  }

  private async initializeComponent(): Promise<void> {
    await this.getEventSource();
    await this.fetchVideoAttributes();
    
    const teamSubs = this.teamService
      .getTeamById(this.data.teamDocId)
      .subscribe((result) => {
        this.channelDetails = result?.channelDetails || {
          defaultTags: [],
          defaultEventId: '',
        };

        this.isTagsEnabled = this.channelDetails.defaultTags !== undefined;
        this.isEventsEnabled = this.channelDetails.defaultEventId !== undefined;

        this.defaultTags = this.channelDetails.defaultTags
          ? [...this.channelDetails.defaultTags as string[]]
          : [];

        if (this.channelDetails.defaultEventId !== undefined) {
          this.findSource(this.channelDetails.defaultEventId as string || '');
        }
        
        this.loadAttributes();
        this.isCreate = false;
        this.isLoading = false;

        teamSubs.unsubscribe();
      });

    this.allTags = await firstValueFrom(
      this.teamService.fetchAllNamedTags(this.data.channelDocId)
    );
  }

  toggleTags() {
    this.isTagsEnabled = !this.isTagsEnabled;
    this.defaultTags = this.isTagsEnabled ? [] : undefined;
  }

  toggleEvents() {
    this.isEventsEnabled = !this.isEventsEnabled;
    if (!this.isEventsEnabled) {
      this.selectedSource = {};
    }
  }

  async fetchVideoAttributes() {
    this.videoAttributes = await this.videoAttributesService.getAttributes(
      this.data.channelDocId 
    );
    this.videoAttributesLength = this.videoAttributes?.mappings ? Object.keys(
      this.videoAttributes?.mappings
    ).length : 0;
  }

  getAttributes(event: AttributeEmitType){
    const {invalidAttributes, attributes} = event;
    this.currentAttributes = attributes;
    this.invalidAttributes = invalidAttributes;
    const defaultAttribute = this.channelDetails?.defaultAttribute;

    if (!defaultAttribute || typeof defaultAttribute !== 'object') {return;}

    this.channelDetails = {
      ...this.channelDetails,
      ...Object.assign(
        {},
        ...AttributeKeys.map(attribute => {
          const key = (defaultAttribute as CustomAttributes)[attribute];
          return attributes[attribute] && key ? { [key]: attributes[attribute] } : {};
        })
      )
    };
  }

  getAttributeName(attr: AttributeKey) {
    return this.videoAttributesService.getAttributeName(attr, this.videoAttributes);
  }

  async loadAttributes() {
    if (!this.channelDetails?.defaultAttribute) {
      this.originalAttributes = {};
      this.currentAttributes = {};
      return;
    }
  
    const result = this.videoAttributesService.setupAttributes(
      this.videoAttributes, 
      this.channelDetails.defaultAttribute as CustomAttributes
    );
    this.addedAttributes = result.addedAttributes;
    this.selectedAttributeValueKeys = result.selectedAttributeValueKeys;
    this.currentAttributes = result.currentAttributes;
    this.originalAttributes = result.currentAttributes || {};
  }

  async getEventSource(){
    if(!this.data.channelDocId) return
    this.eventSourceDocs = await this.masterChannelService.getActiveEventList(this.data.channelDocId);
  }
  
  removeTag(tag: string): void {
    const index = this.defaultTags!.indexOf(tag);
    if (index >= 0) {
      this.defaultTags!.splice(index, 1);
    }
  }

  addTag(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value && this.defaultTags && !this.defaultTags.includes(value)) {
      this.defaultTags.push(value);
    }
    event.chipInput.inputElement.value = ''
    this.tagInputValue = '';
    this.filteredTagSuggestions = [];
  }
  dropDownDivOpen(dropName: string) {
    if (this.isDefaultEventCleared) {
      this.resetSearchText();
      return;
    }
    this.ddName = dropName;
    this.ddOpen = true;
    if (dropName === '') {this.resetSearchText();}
  }
  resetSearchText() {
    this.searchText = '';
    this.filterList = undefined;
    this.isDefaultEventCleared = false;
  }

  findSource(sourceId: string) {
    const selectedSource = this.eventSourceDocs.find(
      (event) => event.docId === sourceId
    );
    this.selectedSource.docId = selectedSource?.docId || '';
    this.selectedSource.name = selectedSource?.name || '';
  }

  filterSearch(name: string, searchText: string) {
    return (
      name?.toString()?.toLowerCase().indexOf(searchText.toLowerCase()) === -1
    );
  }

  setSource(sourceId: string, sourceName: string) {
    this.selectedSource.docId = sourceId;
    this.selectedSource.name = sourceName;
    this.resetSearchText();
  }

  searchEvents() {
    this.filterList = this.eventSourceDocs.filter(
      (event) =>
        event.name
          ?.toString()
          ?.toLowerCase()
          .indexOf(this.searchText.toLowerCase()) != -1
    );
  }
  closeDialog() {
    this.dialogRef.close();
  }

   
  private hasInvalidAttributes(): boolean {
    return this.videoAttributesService.hasInvalidAttributes(this.addedAttributes);
  }

  updateEventsTags() {
    if (this.isSpinner) return;
    if (!globalThis.navigator.onLine) {
      this.dialogService.openInternetLostDialog();
      return;
    }
    this.videoAttributesService.startSave();
    if (this.invalidAttributes || this.hasInvalidAttributes()) {
      return;
    }

    this.isSpinner = true;
    const UpdatedTags = this.isTagsEnabled ? this.defaultTags : [];
    this.isAttributesEnabled= !!(this.currentAttributes && Object.keys(this.currentAttributes).length > 0);
    const UpdatedEventId =
      this.isEventsEnabled && this.selectedSource?.docId
        ? this.selectedSource.docId
        : undefined;
    const UpdatedAttributes = this.isAttributesEnabled 
      ? this.currentAttributes 
      : undefined;
    this.teamService
      .updateTeam(
        this.data.teamDocId,
        UpdatedTags,
        UpdatedEventId,
        this.isTagsEnabled,
        this.isEventsEnabled,
        this.isAttributesEnabled,
        UpdatedAttributes 
      )
      .then(() => {
        this.isSpinner = false;
        this.dialogRef.close('success');
        this.openSuccessPopUp();
      })
      .catch(() => {
        this.isSpinner = false;
      });
  }

  disableUpdate() {  
    if (this.isEventsEnabled && !this.selectedSource.docId){
      return true;
    }
    const tagsEqual = isEqual(
      this.channelDetails?.defaultTags,
      this.isTagsEnabled ? (this.defaultTags || [] ): undefined
    );
  
    const eventsEqual = this.isEventsEnabled
      ? this.channelDetails.defaultEventId === this.selectedSource.docId
      : this.channelDetails.defaultEventId === undefined;
    
    const attributesEqual = isEqual(this.originalAttributes, this.currentAttributes);
    return (tagsEqual && eventsEqual && attributesEqual);
  }
  openSuccessPopUp() {
    this.dialog.open(SuccessPopupComponent, {
      data: {
        successMessage: 'Successfully updated default categories and events',
      },
      width: '468px',
      position: {
        top: '13px',
      },
      disableClose: true,
      panelClass: 'success-message-dialog',
    });
  }

  onTagInputChange() {
    const input = this.tagInputValue?.toLowerCase().trim() || '';
    this.filteredTagSuggestions = this.allTags.filter(
      (tag) =>
        tag.toLowerCase().includes(input) &&
        this.defaultTags &&
        !this.defaultTags.includes(tag)
    );
  }

  selectTag(tag: string) {
    if (this.defaultTags && !this.defaultTags.includes(tag)) {
      this.defaultTags.push(tag);
    }
    this.tagInputValue = '';
    this.filteredTagSuggestions = [];
  }

  onTagInputPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasteData = event.clipboardData?.getData('text') || '';
    
    if (pasteData.trim()) {
      const tags = pasteData.split(',').map(tag => tag.trim()).filter(Boolean);
      
      tags.forEach(tag => {
        if (tag && this.defaultTags && !this.defaultTags.includes(tag)) {
          this.defaultTags.push(tag);
        }
      });
      this.tagInputValue = '';
      this.filteredTagSuggestions = [];
    }
  }

  positionDropdown() {
    const rect = this.tagInput.nativeElement.getBoundingClientRect();
    const dropdown = document.querySelector('.auto-complete') as HTMLElement;
    if (dropdown) {
      dropdown.style.top = `${rect.bottom + window.scrollY}px`;
      dropdown.style.left = `${rect.left + window.scrollX}px`;
    }
  }

  ngAfterViewChecked() {
    if (this.tagInput) {
      this.positionDropdown();
    }
  }
}
